import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, MessageSquare, ShoppingCart, Search, Eye, AlertTriangle, ChevronDown, ChevronUp, Play, Pause } from 'lucide-react';

interface User {
  id: string;
  name: string;
  phone: string;
  created_at: string;
  last_activity: string;
  messages: { count: number }[];
  shopping_lists: { count: number }[];
  bot_enabled?: boolean;
  atendimento?: boolean;
  seller?: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
}

interface UserBotStatus {
  [userId: string]: boolean | undefined;
}

const Usuarios = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [botStatus, setBotStatus] = useState<UserBotStatus>({});
  const [botLoading, setBotLoading] = useState<string | null>(null);

  // Carrega status do bot ao expandir usuário
  const fetchBotStatus = async (userId: string) => {
    setBotLoading(userId);
    try {
      const resp = await fetch(`/api/users/${userId}/bot-status`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
      if (resp.ok) {
        const data = await resp.json();
        setBotStatus(prev => ({ ...prev, [userId]: data.bot_enabled }));
      }
    } catch (err) {
      setBotStatus(prev => ({ ...prev, [userId]: undefined }));
    } finally {
      setBotLoading(null);
    }
  };

  const handleToggleBot = async (userId: string, enabled: boolean) => {
    setBotLoading(userId);
    try {
      const resp = await fetch(`/api/users/${userId}/toggle-bot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ enabled })
      });
      if (resp.ok) {
        setBotStatus(prev => ({ ...prev, [userId]: enabled }));
        // Atualiza o campo bot_enabled do usuário na lista
        setUsers(prevUsers => prevUsers.map(u =>
          u.id === userId ? { ...u, bot_enabled: enabled } : u
        ));
      }
    } finally {
      setBotLoading(null);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      // Usar /api/customers para buscar clientes do WhatsApp
      const response = await fetch('/api/customers', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
        setBackendAvailable(true);
      } else {
        setBackendAvailable(false);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      setBackendAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.phone && user.phone.includes(searchTerm))
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleUserExpansion = (userId: string) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      if (botStatus[userId] === undefined) {
        fetchBotStatus(userId);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-600 mt-1">Gerencie todos os usuários do sistema</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Backend Warning */}
      {!backendAvailable && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Dados Não Disponíveis
              </h3>
              <p className="text-yellow-700">
                Esta é uma demonstração estática. Os dados dos usuários aparecerão aqui quando o backend estiver implantado e funcionando.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Usuários</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <MessageSquare className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Usuários Ativos</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.messages?.[0]?.count > 0).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <ShoppingCart className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Com Listas</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.shopping_lists?.[0]?.count > 0).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Lista de Usuários</h3>
        </div>

        {!backendAvailable ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Backend Não Disponível</h3>
            <p className="mt-1 text-sm text-gray-500">
              Implante o backend para ver os dados dos usuários
            </p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum usuário encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Tente ajustar sua busca' : 'Os usuários aparecerão aqui quando enviarem mensagens'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <div key={user.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-xl font-bold text-white">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    {/* Header Row */}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{user.name}</h3>
                        <p className="text-sm text-gray-600">{user.phone}</p>
                      </div>

                      {/* Status Badge */}
                      <div className="flex-shrink-0">
                        {user.bot_enabled ? (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold text-pink-700 bg-pink-100 border border-pink-200">
                            FORA DE ATENDIMENTO
                          </span>
                        ) : user.atendimento ? (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold text-green-700 bg-green-100 border border-green-200">
                            EM ATENDIMENTO{user.seller?.name ? ` - ${user.seller.name.toUpperCase()}` : ''}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold text-red-700 bg-red-100 border border-red-200">
                            AGUARDANDO
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Info Row */}
                    <div className="flex items-center gap-6 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1.5">
                        <MessageSquare className="h-4 w-4 text-gray-400" />
                        <span>{user.messages?.[0]?.count || 0} msgs</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <ShoppingCart className="h-4 w-4 text-gray-400" />
                        <span>{user.shopping_lists?.[0]?.count || 0} listas</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Cadastrado: {formatDate(user.created_at)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Última atividade: {user.last_activity ? formatDate(user.last_activity) : 'Nunca'}
                      </div>
                    </div>

                    {/* Actions Row */}
                    <div className="flex items-center gap-2">
                      {/* Bot Play/Pause Button */}
                      <button
                        onClick={() => handleToggleBot(user.id, !user.bot_enabled)}
                        disabled={botLoading === user.id}
                        className={`group relative inline-flex items-center justify-center w-10 h-10 rounded-lg transition-all ${
                          user.bot_enabled 
                            ? 'bg-green-100 hover:bg-green-200 text-green-700' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        } ${botLoading === user.id ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                        title={user.bot_enabled ? 'Pausar Bot (Atendimento Manual)' : 'Ativar Bot (Atendimento Automático)'}
                      >
                        {botLoading === user.id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent"></div>
                        ) : user.bot_enabled ? (
                          <Play className="h-5 w-5" />
                        ) : (
                          <Pause className="h-5 w-5" />
                        )}
                        {/* Tooltip */}
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          {user.bot_enabled ? 'Pausar Bot' : 'Ativar Bot'}
                          <svg className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-1" viewBox="0 0 8 4">
                            <path fill="#111827" d="M0 0l4 4 4-4z" />
                          </svg>
                        </span>
                      </button>

                      <button
                        onClick={() => toggleUserExpansion(user.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                      >
                        {expandedUser === user.id ? (
                          <>
                            <ChevronUp className="h-4 w-4" />
                            Recolher
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4" />
                            Expandir
                          </>
                        )}
                      </button>

                      <Link
                        to={`/usuarios/${user.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                      >
                        <Eye className="h-4 w-4" />
                        Ver Detalhes
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedUser === user.id && (
                  <div className="mt-6 pt-6 border-t border-gray-200 ml-18">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Bot Status Info */}
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          {user.bot_enabled ? (
                            <Play className="h-4 w-4 text-green-600" />
                          ) : (
                            <Pause className="h-4 w-4 text-gray-600" />
                          )}
                          Atendimento Automático (Bot)
                        </h4>
                        <p className="text-sm text-gray-700 mb-1">
                          Status: <span className="font-semibold">{user.bot_enabled ? 'Ativo' : 'Pausado (Apenas Humano)'}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {user.bot_enabled 
                            ? 'O bot está respondendo automaticamente as mensagens deste usuário.' 
                            : 'Apenas atendentes humanos podem responder este usuário.'}
                        </p>
                      </div>

                      {/* Shopping Lists Info */}
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4 text-purple-600" />
                          Listas de Compras
                        </h4>
                        <p className="text-sm text-gray-700">
                          Total: <span className="font-semibold">{user.shopping_lists?.[0]?.count || 0} listas criadas</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Clique em "Ver Detalhes" para visualizar todas as listas
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Usuarios;